import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { VibesitesImage } from '@vibesites/ui-primitives';

export interface LightboxPhoto {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  alt_text?: string | null;
  video_url?: string | null;
}

function resolveSrc(photo: LightboxPhoto): string {
  return photo.image || photo.original_url || '';
}

const LIGHTBOX_CSS = `
  .photo-gallery-lightbox {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.85);
    padding: 1rem;
    touch-action: pan-y;
  }

  .photo-gallery-lightbox__img-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: calc(100vw - 8rem);
    max-height: 90vh;
    touch-action: pan-y;
  }

  .photo-gallery-lightbox__img {
    pointer-events: none;
    -webkit-user-drag: none;
    user-select: none;
  }

  .photo-gallery-lightbox__video {
    display: block;
    width: min(1100px, calc(100vw - 8rem));
    max-height: 90vh;
    background: #000;
  }

  .photo-gallery-lightbox__close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    color: #fff;
    font-size: 2.25rem;
    line-height: 1;
    background: none;
    border: none;
    cursor: pointer;
    opacity: 0.85;
    z-index: 10;
    padding: 0.25rem 0.5rem;
  }
  .photo-gallery-lightbox__close:hover { opacity: 1; }

  .photo-gallery-lightbox__arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 4.5rem;
    height: 4.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: rgba(0, 0, 0, 0.35);
    border: none;
    cursor: pointer;
    padding: 0;
    border-radius: 4px;
    opacity: 0.8;
    z-index: 10;
  }
  .photo-gallery-lightbox__arrow:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.6);
  }
  .photo-gallery-lightbox__arrow--prev { left: 0.75rem; }
  .photo-gallery-lightbox__arrow--next { right: 0.75rem; }

  .photo-gallery-lightbox__counter {
    position: absolute;
    bottom: 1.25rem;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.875rem;
    letter-spacing: 0.05em;
    pointer-events: none;
  }

  @media (max-width: 639px) {
    .photo-gallery-lightbox__img-wrap { max-width: 100vw; }
    .photo-gallery-lightbox__video { width: calc(100vw - 2rem); }
    .photo-gallery-lightbox__arrow {
      width: 3rem;
      height: 3rem;
    }
    .photo-gallery-lightbox__arrow--prev { left: 0.25rem; }
    .photo-gallery-lightbox__arrow--next { right: 0.25rem; }
  }
`;

/**
 * Full-screen photo lightbox shared by the Gallery and Amenities pages:
 * arrows, keyboard (←/→/Esc), swipe, backdrop-to-close, and an "n / total" counter.
 */
export function PhotoLightbox({
  images,
  selectedIndex,
  onClose,
  onChange,
}: {
  images: LightboxPhoto[];
  selectedIndex: number | null;
  onClose: () => void;
  onChange: (index: number | null) => void;
}) {
  const total = images.length;

  const goNext = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex + 1) % total);
    },
    [onChange, selectedIndex, total],
  );

  const goPrev = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      e?.stopPropagation();
      onChange(selectedIndex === null ? null : (selectedIndex - 1 + total) % total);
    },
    [onChange, selectedIndex, total],
  );

  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedIndex, goNext, goPrev, onClose]);

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const didSwipe = useRef(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    didSwipe.current = false;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStartX.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const dy =
        touchStartY.current === null ? 0 : e.changedTouches[0].clientY - touchStartY.current;
      touchStartX.current = null;
      touchStartY.current = null;
      if (Math.abs(dx) < 30 || Math.abs(dx) < Math.abs(dy)) return;
      didSwipe.current = true;
      if (dx < 0) goNext();
      else goPrev();
    },
    [goNext, goPrev],
  );

  const handleBackdropClick = useCallback(() => {
    if (didSwipe.current) {
      didSwipe.current = false;
      return;
    }
    onClose();
  }, [onClose]);

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;
  if (!selectedImage || typeof document === 'undefined') return null;

  const src = resolveSrc(selectedImage);

  return createPortal(
    <div
      className="photo-gallery-lightbox"
      onClick={handleBackdropClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Image lightbox"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="photo-gallery-lightbox__close"
        aria-label="Close lightbox"
      >
        &times;
      </button>

      {total > 1 && (
        <button
          type="button"
          onClick={goPrev}
          className="photo-gallery-lightbox__arrow photo-gallery-lightbox__arrow--prev"
          aria-label="Previous image"
        >
          <ChevronLeft aria-hidden="true" size={36} strokeWidth={2.25} />
        </button>
      )}

      <div
        className="photo-gallery-lightbox__img-wrap"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {selectedImage.video_url ? (
          <video
            key={selectedImage.video_url}
            src={selectedImage.video_url}
            poster={src || undefined}
            controls
            autoPlay
            playsInline
            title={selectedImage.title || 'Property video'}
            className="photo-gallery-lightbox__video"
          />
        ) : (
          <VibesitesImage
            url={src}
            alt={selectedImage.alt_text || selectedImage.title || ''}
            sizes="90vw"
            className="max-w-full max-h-[90vh] object-contain photo-gallery-lightbox__img"
          />
        )}
      </div>

      {total > 1 && (
        <button
          type="button"
          onClick={goNext}
          className="photo-gallery-lightbox__arrow photo-gallery-lightbox__arrow--next"
          aria-label="Next image"
        >
          <ChevronRight aria-hidden="true" size={36} strokeWidth={2.25} />
        </button>
      )}

      {total > 1 && (
        <div className="photo-gallery-lightbox__counter" aria-live="polite">
          {(selectedIndex ?? 0) + 1} / {total}
        </div>
      )}

      <style>{LIGHTBOX_CSS}</style>
    </div>,
    document.body,
  );
}

export default PhotoLightbox;
